import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { vi } from './vi'
import { en } from './en'
import type { Dict } from './vi'

export const FALLBACK_LANG = 'en'

interface LanguageEntry {
  label: string
  short: string
  locale: string
  dict: Dict
}

const registry = {
  en: { label: 'English', short: 'EN', locale: 'en-US', dict: en },
  vi: { label: 'Tiếng Việt', short: 'VI', locale: 'vi-VN', dict: vi },
} satisfies Record<string, LanguageEntry>

export type Lang = keyof typeof registry

export const SUPPORTED_LANGS = Object.keys(registry) as Lang[]

export const LANGUAGES = SUPPORTED_LANGS.map((code) => ({
  code,
  label: registry[code].label,
  short: registry[code].short,
  locale: registry[code].locale,
}))

export function isSupportedLang(value: unknown): value is Lang {
  return typeof value === 'string' && (SUPPORTED_LANGS as string[]).includes(value)
}

const STORAGE_KEY = 'moc-market-lang'

/**
 * Startup language detection:
 * 1. Saved preference in localStorage
 * 2. System/browser language from navigator.languages / navigator.language
 * 3. Default fallback to English ('en') if unsupported
 */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isSupportedLang(saved)) return saved
  } catch {
    /* storage blocked */
  }
  try {
    const candidates = [...(navigator.languages ?? []), navigator.language].filter(Boolean)
    for (const candidate of candidates) {
      const base = candidate.slice(0, 2).toLowerCase()
      if (isSupportedLang(base)) return base
    }
  } catch {
    /* no navigator */
  }
  return FALLBACK_LANG
}

function lookup(dict: Dict, path: string): string | undefined {
  const value = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, dict)
  return typeof value === 'string' ? value : undefined
}

interface I18nValue {
  lang: Lang
  locale: string
  setLang: (lang: Lang) => void
  languages: typeof LANGUAGES
  t: (path: string, params?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children, defaultLang }: { children: ReactNode; defaultLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => defaultLang ?? detectLang())

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang)
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    if (!isSupportedLang(next)) return
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* storage blocked */
    }
  }, [])

  const value = useMemo<I18nValue>(() => {
    const entry = registry[lang] ?? registry[FALLBACK_LANG]

    const t = (path: string, params?: Record<string, string | number>) => {
      let out = lookup(entry.dict, path) ?? lookup(registry[FALLBACK_LANG].dict, path) ?? path
      if (params) {
        for (const [key, val] of Object.entries(params)) {
          out = out.split(`{${key}}`).join(String(val))
        }
      }
      return out
    }

    return {
      lang,
      locale: entry.locale,
      setLang,
      languages: LANGUAGES,
      t,
    }
  }, [lang, setLang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>')
  return ctx
}

export function LanguageSwitcher({ className }: { className?: string }) {
  const { lang, setLang, languages } = useI18n()

  return (
    <div className={`flex items-center gap-1 text-[11px] font-bold ${className ?? ''}`}>
      {languages.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          className={`px-1.5 py-0.5 rounded-md transition-colors ${
            lang === l.code
              ? 'bg-accent text-accent-ink font-extrabold'
              : 'text-ink-3 hover:text-ink hover:bg-surface-2'
          }`}
          title={l.label}
        >
          {l.short}
        </button>
      ))}
    </div>
  )
}
